import React from 'react';
import { get, size } from 'global/lib/lodash';
import { Loading, useScreen } from 'global/styles';
import moment from 'moment';
import TypoLabel from 'global/components/typo-label';
import { FlexGridCell, FlexGridIconLabel, FlexGridRow } from 'global/components/flex-grid-list';
import { useDispatch, useSelector } from 'react-redux';
import { removeNewTask, addNewTask } from 'redux/actions/view';
import { stopProp } from '../../../global/utils';
import { PRINT_STATUS_CELL_SX, PRINT_TASK_ROW_SX } from './TaskListSC.styles';

const STACK_SKIP_IDS = new Set(['_check', 'type', 'title', 'status']);

export default function TaskListItem(props) {
  const { item, tableRows } = props;
  const newTasks = useSelector((state) => get(state, 'view.task.new'));
  const dispatch = useDispatch();
  const isSelected = newTasks.find((t) => t.id === item.id);

  const { isDown: isStacked } = useScreen('lg');
  const { isDown: isXs } = useScreen('sm');

  const content = {
    ...item,
    isSelected,
    realmType: get(item, 'type', Loading),
    label: get(item, 'realm.name', Loading),
    realmName: get(item, 'label', Loading),
    updatedDate: get(item, 'date', Loading),
    agingDate: get(item, 'createdAt', Loading),
    checkTitles: get(item, 'checkTitle', 'Some content'),
    author: get(item, 'authors', [])
      .map((a) => get(a, 'name'))
      .join(', '),
    assignee: get(item, 'assignees', [])
      .map((a) => get(a, 'name'))
      .join(', '),
    standard: get(item, 'checkpoint.id') ? `${get(item, 'checkpoint.id', '')}: ${get(item, 'checkpoint.name', '')}` : Loading,
    tradeTypes: get(item, 'tradeTypes'),
    onCheckClick: () => {
      if (isSelected) {
        dispatch(removeNewTask(item));
      } else {
        dispatch(addNewTask({ ...item, fromSelected: true }));
      }
    },
    onSelect: s => {
      stopProp(s);
    },
    onClick: e => {
      props.onClick && props.onClick(e, item)
    }
  };

  const dayDiff = Math.abs(moment.utc(content.agingDate).startOf('day').diff(moment.utc().startOf('day'), 'days'));
  content.agingDate = `${dayDiff} days`;
  const checkRow = tableRows[0];
  const typeRow = tableRows.find((tr) => tr.id === 'type');
  const statusRow = tableRows.find((tr) => tr.id === 'status');
  const hasTrades = !!size(get(content, 'tradeTypes', []));
  const stackedRows = tableRows.filter((tr) => {
    if (STACK_SKIP_IDS.has(tr.id)) return false;
    if (tr.id === 'trades') return hasTrades;
    return true;
  });

  return (
    <FlexGridRow
      button
      divider
      disableGutters
      layout="columns"
      alignItems={isStacked ? 'flex-start' : 'center'}
      data-testid="task-list-item"
      onClick={content.onClick}
      sx={{ p: 0, pageBreakInside: 'avoid', fontSize: '0.75rem', fontWeight: 500, color: 'text.primary', ...PRINT_TASK_ROW_SX }}
    >
      {isStacked ? (
        <>
          <FlexGridCell width="42px" minWidth="42px" sx={{ overflow: 'visible', pt: 0.5 }}>
            {checkRow.val(content)}
          </FlexGridCell>
          <FlexGridCell
            flexGrow={1}
            sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 0.5, minWidth: 0, overflow: 'visible', py: 0.5 }}
          >
            <FlexGridIconLabel
              icon={typeRow ? typeRow.val(content) : null}
              data-testid="task-list-item-title"
              sx={{ '& .MuiTypography-root': { fontWeight: 700 } }}
            >
              {get(content, 'name')}
            </FlexGridIconLabel>
            {stackedRows.map((tr, i) =>
              tr.id === 'trades' ? (
                <div key={tr.id || i} data-testid="task-list-item-trades">
                  {tr.val(content)}
                </div>
              ) : (
                <TypoLabel
                  key={tr.id || i}
                  primary={isXs && (tr.id === 'location' || tr.id === 'age') ? null : `${tr.title}: `}
                  secondary={tr.val(content)}
                />
              )
            )}
          </FlexGridCell>
          <FlexGridCell
            data-testid="task-list-item-status"
            onClick={stopProp}
            sx={{
              flex: '0 0 auto',
              width: 'auto',
              maxWidth: 'none',
              minWidth: 'auto',
              justifyContent: 'flex-end',
              overflow: 'visible',
              pt: 0.5,
              '& > div': { mb: 0 },
            }}
          >
            {statusRow ? statusRow.val(content) : null}
          </FlexGridCell>
        </>
      ) : (
        tableRows.map((tr, i) => (
          <FlexGridCell
            key={i}
            width={tr.width}
            data-testid={tr.id ? `task-list-item-${tr.id}` : undefined}
            sx={{
              flexShrink: 1,
              ...(tr.style || {}),
              ...(tr.id === 'status' ? PRINT_STATUS_CELL_SX : null),
            }}
          >
            {tr.val(content)}
          </FlexGridCell>
        ))
      )}
    </FlexGridRow>
  );
}
